import AppsIcon from '@mui/icons-material/Apps';
import { Box, Grid, Stack } from '@mui/material';
import { isEmpty, omit } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  getJobFieldSettings,
  IJobFieldSettings,
  IJobFieldSettingsType,
  saveJobFieldSettings,
} from 'wisestep-api';
import { ControlledTextInput, Label, WsButton, WsListingLoader } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import HeaderBox from '../../../features/crmSettings/components/HeaderBox';
import {
  EditIconButton,
  DeleteIconButton,
  SaveIconButton,
} from '../../../features/crmSettings/components/StyledIconButtons';
import { getCategories, IJobCategory } from '../../../features/jobs/helper/JobHelperService';
import { JOB_CATEGORY_DEFAULT } from '../../../features/templateSettings/helper/constants';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

import { getDefaultJobFieldSettings } from './Helper';
import JobCategoryEditPopup from './JobCategoryEditPopup';

const CustomizeJobCategoryDialog = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openOccupationDialog, setOpenOccupationDialog] = useState(false);
  const [selectedCategoryIndex, setSelecteCategoryIndex] = useState<number>(-1);
  const [jobCategoryData, setJobCategoryData] = useState<IJobFieldSettingsType>({
    mandatory: false,
    hide: true,
    disabled: false,
    label: 'Job Category',
    availableFields: JOB_CATEGORY_DEFAULT,
    selectedField: '',
    selectedFields: JOB_CATEGORY_DEFAULT,
  });
  const occupationDialogClose = () => {
    setOpenOccupationDialog(false);
    setSelecteCategoryIndex(-1);
  };

  const jobCategories = getCategories();
  const [loader, setLoader] = useState<boolean>(false);
  const [selectedCategories, setSelectedCategories] = useState<IJobCategory[]>(jobCategories);
  const [editingIndex, setEditingIndex] = useState<number>();
  const [jobFieldSettingsData, setJobFieldSettingsData] = useState<IJobFieldSettings>(
    getDefaultJobFieldSettings()
  );
  const [modifiedData, setModifiedData] = useState<
    Omit<
      IJobFieldSettings,
      'id' | 'workspaceId' | 'createdBy' | 'createdOn' | 'modifiedBy' | 'modifiedOn'
    > & { editCategoryName?: string }
  >(getDefaultJobFieldSettings());

  useEffect(() => {
    const defaultData = {
      mandatory: false,
      hide: true,
      disabled: false,
      label: 'Job Category',
      availableFields: JOB_CATEGORY_DEFAULT,
      selectedField: '',
      selectedFields: JOB_CATEGORY_DEFAULT,
    };
    if (modifiedData?.jobCategory) {
      const jobCategoriesDefaultValues = modifiedData?.jobCategory;
      setJobCategoryData({ ...jobCategoriesDefaultValues });
    } else {
      setJobCategoryData(defaultData);
    }
  }, [modifiedData?.jobCategory]);

  const methods = useForm({ defaultValues: modifiedData });

  const onStatusEdit = (categoryIndex) => {
    setSelecteCategoryIndex(categoryIndex);
    setOpenOccupationDialog(true);
  };

  const [edit, setEdit] = useState<number>(-1);
  const { reset } = methods;
  const onChange = (index: number, data?: FieldValues) => {
    switch (index) {
      case 0:
        if (data?.jobCategoryOptions) {
          jobCategoryData.selectedFields.push(data.jobCategoryOptions);
        }
        break;
      case 1:
        if (data?.newOption && data?.jobCategoryOptions !== data?.newOption) {
          jobCategoryData.selectedFields[edit] = data?.newOption;
        }
        break;
      case 2:
        jobCategoryData.selectedFields.splice(edit, 1);
        break;
    }
    const newOptionData = jobCategoryData;
    setJobCategoryData({ ...newOptionData });
    setEdit(-1);
    reset();
  };

  const fetchJobFieldSettings = async () => {
    try {
      setLoader(true);
      const getJobFieldSettingsReq = await getJobFieldSettings();
      if (!isEmpty(getJobFieldSettingsReq.data)) {
        if (getJobFieldSettingsReq.data.id) {
          setJobFieldSettingsData(getJobFieldSettingsReq.data);
          const categories = getJobFieldSettingsReq.data.jobCategory;
          const selectedFields = categories.selectedFields?.map((category) => JSON.parse(category));
          selectedFields?.length > 0 && setSelectedCategories(selectedFields);
          setJobCategoryData({ ...categories, selectedFields: selectedFields });
          setLoader(false);
        }
      }
      setLoader(false);
    } catch (e) {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchJobFieldSettings();
  }, []);

  useEffect(() => {
    const payload = omit(jobFieldSettingsData, [
      'id',
      'workspaceId',
      'createdBy',
      'createdOn',
      'modifiedBy',
      'modifiedOn',
    ]);
    if (jobFieldSettingsData?.id) setModifiedData({ ...payload });
    else setModifiedData(getDefaultJobFieldSettings());
    reset(payload);
  }, [jobFieldSettingsData, reset]);

  const onSaveHandler = async (formData: FieldValues) => {
    if (jobFieldSettingsData) {
      try {
        const tempData = { ...formData };
        if (jobFieldSettingsData?.id) {
          tempData.id = jobFieldSettingsData.id;
          tempData.workspaceID = jobFieldSettingsData.workspaceId;
          tempData.createdBy = jobFieldSettingsData.createdBy;
          tempData.createdOn = jobFieldSettingsData.createdOn;
          tempData.modifiedBy = jobFieldSettingsData.modifiedBy;
          tempData.modifiedOn = jobFieldSettingsData.modifiedOn;
          jobCategoryData.selectedFields = selectedCategories?.map((category) =>
            JSON.stringify(category)
          );
          tempData.jobCategory = jobCategoryData;
        }
        await saveJobFieldSettings(tempData as IJobFieldSettings);
        fetchJobFieldSettings();
        setNotification(success('Configure Job Categories,Settings updated successfully'));
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  const onAddNewCategory = () => {
    const newCategory: IJobCategory = {
      value: methods.getValues('editCategoryName') ?? '',
      label: methods.getValues('editCategoryName') ?? '',
      occupations: [],
    };
    setSelectedCategories([newCategory, ...selectedCategories]);
    setEditingIndex(0);
  };
  const handleEditClick = (index) => setEditingIndex(index);

  const handleDeleteClick = (index: number) => {
    const newCategory = [...selectedCategories];
    newCategory.splice(index, 1);
    setSelectedCategories(
      newCategory.map((category, newIndex) => ({ ...category, index: newIndex }))
    );
  };

  const handleOccupationDataUpdate = (data) => {
    const updatedCategories = [...selectedCategories];
    updatedCategories[selectedCategoryIndex] = data;
    selectedCategoryIndex > -1 && setSelectedCategories(updatedCategories);
  };

  const updateCategoryData = (e, index) => {
    setSelectedCategories((prevCategories) =>
      prevCategories.map((category, currentIndex) => {
        if (currentIndex === index) {
          return { ...category, label: e.target.value, value: e.target.value };
        }
        return category;
      })
    );
  };

  const handleSaveClick = (index) => setEditingIndex(index);
  const handleClearUnsavedChanges = () => {
    setSelectedCategories(
      jobCategoryData.selectedFields.map((jsonString) => JSON.parse(jsonString))
    );
    setEditingIndex(0);
  };

  return (
    <SettingsPageLayout
      title="Job Settings"
      indexOptions={[
        { label: 'INDEX', url: '' },
        {
          label: 'Job Category',
          url: '/job-category-configuration',
          icon: <AppsIcon color="action" />,
        },
      ]}
      backUrl={'/job-fields-settings'}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSaveHandler)}>
          <Box px={8} pt={2} display="flex" flexDirection="column">
            <HeaderBox
              title="Job Category Configuration"
              includeBtn={true}
              btnText="Add New Category"
              onClick={onAddNewCategory}
            />
            <Stack mb={2} justifyContent="end" display="flex" direction="row" spacing={2}>
              <WsButton onClick={handleClearUnsavedChanges} variant="contained">
                Clear Unsaved Changes
              </WsButton>
              <WsButton variant="contained" type="submit">
                Save Everything
              </WsButton>
            </Stack>
            {!loader ? (
              <>
                {selectedCategories?.map((category, index) => (
                  <Grid container justifyContent="space-between" key={index} item xs={12}>
                    <Grid item xs={6} display="flex">
                      {editingIndex === index ? (
                        <Grid item xs={12} container>
                          <ControlledTextInput
                            name="editCategoryName"
                            value={category.label}
                            onChange={(e) => updateCategoryData(e, index)}
                          />
                        </Grid>
                      ) : (
                        <Label sx={{ fontWeight: 'bold', minWidth: '100%', textAlign: 'start' }}>
                          {category.label}
                        </Label>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      <WsButton fullWidth variant="outlined" onClick={() => onStatusEdit(index)}>
                        Show Occupation List
                      </WsButton>
                    </Grid>
                    <Grid item xs={2} display="flex" justifyContent="end">
                      {editingIndex === index ? (
                        <>
                          <EditIconButton onClick={() => onChange(1)} />
                          <SaveIconButton onClick={() => handleSaveClick(-1)} />
                        </>
                      ) : (
                        <EditIconButton onClick={() => handleEditClick(index)} />
                      )}
                      <DeleteIconButton onClick={() => handleDeleteClick(index)} />
                    </Grid>
                  </Grid>
                ))}
              </>
            ) : (
              <WsListingLoader />
            )}
          </Box>
        </form>
        {selectedCategories && selectedCategoryIndex > -1 && (
          <JobCategoryEditPopup
            open={openOccupationDialog}
            closeDialog={occupationDialogClose}
            jobCategoryData={selectedCategories[selectedCategoryIndex]}
            onDataUpdate={handleOccupationDataUpdate}
          />
        )}
      </FormProvider>
    </SettingsPageLayout>
  );
};

export default CustomizeJobCategoryDialog;
